/**
 * 可视化组件目录（分组 + 扁平索引，供侧栏 / Hub / 锚点跳转）
 * @typedef {'atom' | 'atom-fill' | 'half' | 'panel' | 'panel-fit' | 'panel-fill' | 'wide-fill' | 'half-chart' | 'full' | 'chart' | 'table' | 'summary' | 'summary-wide' | 'rail' | 'band' | 'band-wide' | 'milestone-rail' | 'milestone-stack' | 'milestone-side-card' | 'resource-schedule-row' | 'work-progress-mini' | 'dashboard-vis-row' | 'matter-reminder' | 'loss-kpi' | 'loss-chart' | 'loss-dashboard-full'} VisualStageSize
 * @typedef {{ id: string, title: string, hash: string, component: string, desc: string, stage: VisualStageSize, span?: number }} VisualCatalogItem
 * @typedef {{ key: string, title: string, subtitle: string, layout: 'atoms' | 'duo' | 'stack', specKey?: string, items: VisualCatalogItem[] }} VisualCatalogGroup
 */

/** @type {VisualCatalogGroup[]} */
export const VISUAL_COMPONENT_GROUPS = [
  {
    key: 'metric-atoms',
    title: '指标原子',
    subtitle: '数值、触发器、趋势符号等最小单元，可嵌入表格或 KPI 条拼装。',
    layout: 'atoms',
    items: [
      {
        id: 'count-up',
        title: '数字滚动',
        hash: '#biz-vis-count-up',
        component: 'PmCountUp',
        desc: 'Hero / Detail / Summary 三档字号，支持 reduced-motion。',
        stage: 'atom',
      },
      {
        id: 'kpi-popover',
        title: '指标说明触发器',
        hash: '#biz-vis-kpi-popover',
        component: 'PmKpiPopover',
        desc: 'Hero + Detail 双档触发器，点击弹出说明。',
        stage: 'atom-fill',
      },
      {
        id: 'spark-trend-arrow',
        title: '趋势箭头',
        hash: '#biz-vis-spark-trend-arrow',
        component: 'PmSparkTrendArrow',
        desc: '环比 / 同比旁的上行、下行箭头与量值胶囊。',
        stage: 'atom-fill',
      },
      {
        id: 'loss-bar',
        title: '损耗量率组合',
        hash: '#biz-vis-loss-bar',
        component: 'PmLossBar',
        desc: '原材表内「量 / 率」并列，planned / actual / over 语义色。',
        stage: 'atom',
      },
      {
        id: 'kpi-stat-hero',
        title: 'Hero 强调条',
        hash: '#biz-vis-kpi-stat-hero',
        component: 'PmDashKpiStatCell',
        desc: '三格并排强调指标，图标 + 大数 + 语义色。',
        stage: 'panel',
        span: 2,
      },
      {
        id: 'kpi-stat-detail',
        title: 'Detail 明细条',
        hash: '#biz-vis-kpi-stat-detail',
        component: 'PmDashKpiStatCell',
        desc: '三格并排明细格，含环形占比 orb。',
        stage: 'panel',
        span: 2,
      },
    ],
  },
  {
    key: 'metric-blocks',
    title: '指标组合',
    subtitle: '横向汇总条、竖向汇总轨与混凝土 KPI 整带。',
    layout: 'stack',
    items: [
      {
        id: 'kpi-summary',
        title: 'KPI 汇总条（横条）',
        hash: '#biz-vis-kpi-summary',
        component: 'PmKpiSummaryCard',
        desc: 'bar 布局：超耗应扣 / 损耗降低 / 风险预警三区横排。',
        stage: 'summary-wide',
      },
      {
        id: 'kpi-summary-rail',
        title: 'KPI 汇总轨（竖条）',
        hash: '#biz-vis-kpi-summary-rail',
        component: 'PmKpiSummaryCard',
        desc: 'rail 布局：竖向标题 + 指标叠放，用于侧栏汇总轨。',
        stage: 'rail',
      },
      {
        id: 'cm-kpi-band',
        title: '混凝土 KPI 整带',
        hash: '#biz-vis-cm-kpi-band',
        component: 'PmCmKpiBand',
        desc: 'Hero + 走势 + 明细 + 底栏汇总，图表折叠态。',
        stage: 'band-wide',
      },
      {
        id: 'home-overview-kpi-band',
        title: '首页 KPI 整带',
        hash: '#biz-vis-home-overview-kpi-band',
        component: 'PmHomeOverviewKpiBand',
        desc: '工期进度 + 产值确认率 + 利润率 / 现金流火花卡 + 资金状态。',
        stage: 'wide-fill',
        span: 2,
      },
      {
        id: 'home-progress-kpi',
        title: '工期进度条',
        hash: '#biz-vis-home-progress-kpi',
        component: 'PmHomeProgressKpi',
        desc: '三指标 + 渐变进度条 + 已施工 / 总工期脚注。',
        stage: 'panel-fit',
      },
      {
        id: 'home-spark-metric-card',
        title: '首页火花指标卡',
        hash: '#biz-vis-home-spark-metric-card',
        component: 'PmHomeSparkMetricCard',
        desc: '标题趋势胶囊 + 大数 + 迷你火花图 + 右侧确权产值列表。',
        stage: 'panel-fit',
      },
      {
        id: 'home-finance-status',
        title: '资金状态双行',
        hash: '#biz-vis-home-finance-status',
        component: 'PmHomeFinanceStatus',
        desc: '确权未回款 / 分包待付款，勾选 orb + 万元大数。',
        stage: 'panel-fit',
      },
      {
        id: 'output-value-summary-card',
        title: '产值汇总卡',
        hash: '#biz-vis-output-value-summary-card',
        component: 'PmOutputValueSummaryCard',
        desc: '柱图 + 上下两块指标区：确权率 / 较目标 / 差额（1:76837）。',
        stage: 'panel-fit',
      },
    ],
  },
  {
    key: 'milestone-rail',
    title: '里程碑侧栏',
    subtitle: '里程碑总览竖轨、节点状态小卡与延期提醒条。',
    layout: 'duo',
    items: [
      {
        id: 'milestone-overview-panel',
        title: '里程碑总览侧栏',
        hash: '#biz-vis-milestone-overview-panel',
        component: 'PmMilestoneOverviewPanel',
        desc: '列表 / 网格切换 + 延期提醒 + 分组折叠 + 节点卡列表。',
        stage: 'milestone-rail',
      },
      {
        id: 'milestone-card-stack',
        title: '里程碑节点卡栈',
        hash: '#biz-vis-milestone-card-stack',
        component: 'PmMilestoneCardStack',
        desc: '五色状态小卡竖向叠放（绿 / 红 / 蓝语义）。',
        stage: 'milestone-stack',
      },
      {
        id: 'milestone-side-card',
        title: '里程碑节点小卡',
        hash: '#biz-vis-milestone-side-card',
        component: 'PmMilestoneSideCard',
        desc: '左侧色条 + 状态胶囊 + 要求 / 预测日期 + 延期天数大数。',
        stage: 'milestone-side-card',
        span: 2,
      },
      {
        id: 'resource-schedule-card',
        title: '资源进度汇总卡',
        hash: '#biz-vis-resource-schedule-card',
        component: 'PmResourceScheduleCard',
        desc: '资源表 + 责任偏差区 + 计划/实际/预测工期，三色边框卡。',
        stage: 'resource-schedule-row',
        span: 2,
      },
      {
        id: 'work-item-progress-card',
        title: '工项进度迷你卡',
        hash: '#biz-vis-work-item-progress-card',
        component: 'PmWorkItemProgressCard',
        desc: '标题 + 预测状态 + 偏差/措施 + 完成率 + 底栏进度条 + 侧向添加钮。',
        stage: 'work-progress-mini',
      },
    ],
  },
  {
    key: 'dashboard-vis',
    title: '看板可视化',
    subtitle: '商务页看板可视化组件：趋势迷你柱、环形 KPI 等。',
    layout: 'duo',
    items: [
      {
        id: 'dash-trend-metric-card',
        title: '看板趋势指标卡',
        hash: '#biz-vis-dash-trend-metric-card',
        component: 'PmDashTrendMetricCard',
        desc: '批复率面板：标题 + 主指标 + 图例胶囊 + Y 轴网格 + 分组柱图（1:85577）。',
        stage: 'dashboard-vis-row',
        span: 2,
      },
      {
        id: 'dash-donut-metric-card',
        title: '看板环形指标卡',
        hash: '#biz-vis-dash-donut-metric-card',
        component: 'PmDashDonutMetricCard',
        desc: '环形占比 + 右侧双行指标说明，蓝 / 红 / 绿三色。',
        stage: 'dashboard-vis-row',
      },
      {
        id: 'matter-reminder-card',
        title: '事项提醒卡',
        hash: '#biz-vis-matter-reminder-card',
        component: 'PmMatterReminderCard',
        desc: '变更洽商 / 现场签证等待办条数，蓝字未报出 / 未批复计数。',
        stage: 'matter-reminder',
      },
    ],
  },
  {
    key: 'spark-charts',
    title: '走势与火花图',
    subtitle: '洞察卡、迷你折线、面积火花等轻量趋势可视化。',
    layout: 'duo',
    items: [
      {
        id: 'spark-insight',
        title: '走势洞察卡（折叠态）',
        hash: '#biz-vis-spark-insight',
        component: 'PmSparkInsightRow',
        desc: 'tall 双卡：损耗率 / 超耗率，适配图表收起后的竖向空间。',
        stage: 'panel-fill',
      },
      {
        id: 'spark-insight-expanded',
        title: '走势洞察卡（展开态）',
        hash: '#biz-vis-spark-insight-expanded',
        component: 'PmSparkInsightRow',
        desc: '标准 grid：卡内嵌迷你折线 + 分项损耗条 + 侧栏量值。',
        stage: 'panel',
      },
      {
        id: 'spark-insight-materials',
        title: '原材走势卡矩阵',
        hash: '#biz-vis-spark-insight-materials',
        component: 'PmSparkInsightRow',
        desc: 'materials 布局：六色原材卡，3×2 矩阵撑满宽屏。',
        stage: 'wide-fill',
        span: 2,
      },
      {
        id: 'insight-mini-chart',
        title: '洞察迷你折线图',
        hash: '#biz-vis-insight-mini-chart',
        component: 'PmInsightMiniChart',
        desc: '多序列折线 + 目标线 + 高亮点位。',
        stage: 'half',
      },
      {
        id: 'insight-mini-chart-danger',
        title: '洞察迷你图（超耗）',
        hash: '#biz-vis-insight-mini-chart-danger',
        component: 'PmInsightMiniChart',
        desc: '超耗率序列 + 管控上限参考线，危险色系。',
        stage: 'half',
      },
      {
        id: 'spark-area',
        title: '火花面积条',
        hash: '#biz-vis-spark-area',
        component: 'PmSparkArea',
        desc: '轻量 ECharts 面积折线，多色趋势条对比。',
        stage: 'half',
        span: 2,
      },
      {
        id: 'raw-material-insight',
        title: '原材走势列表',
        hash: '#biz-vis-raw-material-insight',
        component: 'PmRawMaterialInsightList',
        desc: '四色原材行 + 迷你图，全宽展示损耗指标。',
        stage: 'wide-fill',
        span: 2,
      },
    ],
  },
  {
    key: 'dashboard-charts',
    title: '大盘图表',
    subtitle: '筛选工具栏、折叠拉杆与全宽多维趋势图，撑满看板图表区。',
    layout: 'stack',
    items: [
      {
        id: 'chart-toolbar',
        title: '图表筛选工具栏',
        hash: '#biz-vis-chart-toolbar',
        component: 'PmChartToolbar',
        desc: '统计内容 / 材料分类 / 统计方式 + 可滚动图例。',
        stage: 'full',
      },
      {
        id: 'panel-fold-bar',
        title: '面板折叠拉杆',
        hash: '#biz-vis-panel-fold-bar',
        component: 'PmPanelFoldBar',
        desc: 'KPI 区与趋势图之间的展开 / 收起控制。',
        stage: 'full',
      },
      {
        id: 'trend-chart-raw',
        title: '原材料多维趋势图',
        hash: '#biz-vis-trend-chart-raw',
        component: 'PmOverviewTrendChart',
        desc: '按材料分类多序列折线，支持按月 / 按盘点切换。',
        stage: 'chart',
      },
      {
        id: 'trend-chart-cm',
        title: '混凝土多维趋势图',
        hash: '#biz-vis-trend-chart-cm',
        component: 'PmOverviewTrendChart',
        desc: '按结构物分类（桩基 / 初支）损耗率 / 超耗率趋势。',
        stage: 'chart',
      },
    ],
  },
  {
    key: 'chart-gallery',
    title: '扩展图表示例',
    subtitle: '横向条、堆叠柱、多环占比、面积、多环仪表、雷达、散点、瀑布、热力、柱线、玫瑰、漏斗、树图等，色板对齐 DT34。',
    layout: 'duo',
    items: [
      {
        id: 'vis-chart-horizontal-bar',
        title: '横向条形对比',
        hash: '#biz-vis-chart-horizontal-bar',
        component: 'PmVisShowcaseChart',
        desc: '分项损耗率横向对比，多色条形 + 末端标注。',
        stage: 'half-chart',
      },
      {
        id: 'vis-chart-stacked-bar',
        title: '堆叠柱状图',
        hash: '#biz-vis-chart-stacked-bar',
        component: 'PmVisShowcaseChart',
        desc: '月度方量构成堆叠柱，纯色分层易区分。',
        stage: 'half-chart',
      },
      {
        id: 'vis-chart-donut',
        title: '双环占比图',
        hash: '#biz-vis-chart-donut',
        component: 'PmVisShowcaseChart',
        desc: '内环损耗构成 + 外环材料占比，微弱渐变双环。',
        stage: 'half-chart',
      },
      {
        id: 'vis-chart-area-trend',
        title: '多序列面积趋势',
        hash: '#biz-vis-chart-area-trend',
        component: 'PmVisShowcaseChart',
        desc: '产量 / 损耗 / 回收三序列渐变面积。',
        stage: 'half-chart',
      },
      {
        id: 'vis-chart-gauge',
        title: '损耗率仪表盘',
        hash: '#biz-vis-chart-gauge',
        component: 'PmVisShowcaseChart',
        desc: '外环语义色带 + 主环进度，中心大数与底部基准说明。',
        stage: 'half-chart',
      },
      {
        id: 'vis-chart-radar',
        title: '多维指标雷达',
        hash: '#biz-vis-chart-radar',
        component: 'PmVisShowcaseChart',
        desc: '五维雷达对比本月与上月。',
        stage: 'half-chart',
      },
      {
        id: 'vis-chart-scatter',
        title: '方量散点相关',
        hash: '#biz-vis-chart-scatter',
        component: 'PmVisShowcaseChart',
        desc: '方量 vs 损耗率散点，双色系分结构物。',
        stage: 'half-chart',
      },
      {
        id: 'vis-chart-waterfall',
        title: '超耗瀑布分解',
        hash: '#biz-vis-chart-waterfall',
        component: 'PmVisShowcaseChart',
        desc: '合理 → 分项增量 → 实际的瀑布柱。',
        stage: 'half-chart',
      },
      {
        id: 'vis-chart-heatmap',
        title: '周度热力矩阵',
        hash: '#biz-vis-chart-heatmap',
        component: 'PmVisShowcaseChart',
        desc: '原材 × 星期热力格，蓝红渐变映射。',
        stage: 'half-chart',
      },
      {
        id: 'vis-chart-mixed-bar-line',
        title: '柱线混合图',
        hash: '#biz-vis-chart-mixed-bar-line',
        component: 'PmVisShowcaseChart',
        desc: '产量柱 + 损耗率折线双 Y 轴。',
        stage: 'half-chart',
      },
      {
        id: 'vis-chart-rose',
        title: '玫瑰图',
        hash: '#biz-vis-chart-rose',
        component: 'PmVisShowcaseChart',
        desc: '原材损耗半径玫瑰，扇区面积映射占比。',
        stage: 'half-chart',
      },
      {
        id: 'vis-chart-funnel',
        title: '损耗漏斗图',
        hash: '#biz-vis-chart-funnel',
        component: 'PmVisShowcaseChart',
        desc: '设计方量 → 超耗 → 整改的漏斗转化。',
        stage: 'half-chart',
      },
      {
        id: 'vis-chart-treemap',
        title: '矩形树图',
        hash: '#biz-vis-chart-treemap',
        component: 'PmVisShowcaseChart',
        desc: '原材库存面积映射，色块分区一目了然。',
        stage: 'half-chart',
      },
    ],
  },
  {
    key: 'cable-loss-dashboard',
    title: '电缆损耗看板',
    subtitle: 'DesignTest35 机电损耗管理 · 双 KPI + 四图表；统一 8px 间距，见规范面板。',
    layout: 'duo',
    specKey: 'loss-dashboard',
    items: [
      {
        id: 'loss-dashboard-full',
        title: '电缆损耗看板（整板）',
        hash: '#biz-vis-loss-dashboard-full',
        component: 'PmLossDashboard',
        desc: 'PmLossDashboard：KPI 行 + 2×2 图表网格，与已完成页 dt35-mep-loss-management 同构。',
        stage: 'loss-dashboard-full',
        span: 2,
      },
      {
        id: 'loss-project-kpi',
        title: '项目整体电缆损耗率',
        hash: '#biz-vis-loss-project-kpi',
        component: 'PmLossKpiCard',
        desc: 'danger 语义 · Hero 36px + 四格汇总（描边 Greyscale/300）+ 32px 告警条。',
        stage: 'loss-kpi',
        span: 2,
      },
      {
        id: 'loss-over-reel-kpi',
        title: '超耗电缆盘数（>5%）',
        hash: '#biz-vis-loss-over-reel-kpi',
        component: 'PmLossKpiCard',
        desc: 'warning 语义 · 主行 Hero 垂直居中 · 数字/趋势底对齐。',
        stage: 'loss-kpi',
        span: 2,
      },
      {
        id: 'loss-trend-chart',
        title: '损耗率趋势（近12周）',
        hash: '#biz-vis-loss-trend-chart',
        component: 'PmLossChartCard',
        desc: '折线 + 目标虚线 + PMSmart 接入里程碑 + dataZoom 8 周视窗。',
        stage: 'loss-chart',
      },
      {
        id: 'loss-team-ranking-chart',
        title: '班组损耗率排行',
        hash: '#biz-vis-loss-team-ranking-chart',
        component: 'PmLossChartCard',
        desc: '四色班组柱 + 目标线 / 项目均值参考线。',
        stage: 'loss-chart',
      },
      {
        id: 'loss-spec-compare-chart',
        title: '各规格型号损耗率对比',
        hash: '#biz-vis-loss-spec-compare-chart',
        component: 'PmLossChartCard',
        desc: '柱 + 走势线组合，5% 管控阈值 markLine。',
        stage: 'loss-chart',
      },
      {
        id: 'loss-area-compare-chart',
        title: '各施工部位电缆预算与实际用量对比',
        hash: '#biz-vis-loss-area-compare-chart',
        component: 'PmLossChartCard',
        desc: '预算 / 实际分组柱 + 差异率端点标注。',
        stage: 'loss-chart',
      },
    ],
  },
  {
    key: 'data-table',
    title: '数据表格',
    subtitle: 'Dashboard 内嵌损耗对比表，与走势区、汇总条联动。',
    layout: 'stack',
    items: [
      {
        id: 'raw-mat-table',
        title: '原材损耗表',
        hash: '#biz-vis-raw-mat-table',
        component: 'PmRawMatTable',
        desc: '分类 × 合理 / 实际 / 超耗列，含量率组合与滚动行。',
        stage: 'table',
      },
    ],
  },
];

/** @type {VisualCatalogItem[]} */
export const VISUAL_COMPONENT_CATALOG = VISUAL_COMPONENT_GROUPS.flatMap((g) => g.items);
